import * as XLSX from "xlsx";
import {
	AsrsUploadSingleColumnRequired,
	ValidateAsrsUploadColumnCount,
} from "lib/asrs/uploadLayout";
import { ValidateFlexNetUploadColumnCount } from "lib/flexnet/uploadTemplate";
import * as util from "lib/util";

const maxAllowedCols = 2;

export type ProcessFileOptions = {
	/**
	 * FlexNet PN gate layout. `true` = 2 cols (PN + Serial); `false` = 1 col
	 * (Serial only). `null`/`undefined` skips the FlexNet layout check.
	 */
	partNumberRequired?: boolean | null;
	/** When true, enforce ASRS single-column layout (Serial Number or LPN). */
	asrsUpload?: boolean;
	/** ASRS copy: LPN vs Serial Number. Ignored unless `asrsUpload` is true. */
	byLpn?: boolean;
};

export function IsJsonValid(json: any): json is Record<string, string> {
	if (!json) return false;
	return !!Object.keys(json).length;
}

export function ProcessFile(
	file: File,
	rowLimit = 10,
	options?: ProcessFileOptions
): Promise<Record<string, string>[]> {
	return new Promise((resolve, reject) => {
		// Validate file extension
		if (!file.name.toLowerCase().endsWith(".xlsx")) {
			return reject(
				"Invalid file extension. Only Excel files (.xlsx) are supported."
			);
		}

		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				let data: Uint8Array | null = null;
				if (e.target && e.target.result) {
					data = new Uint8Array(e.target.result as ArrayBuffer);
				}

				if (!data) return reject("Error reading file data");

				const readOptions: XLSX.ParsingOptions = {
					type: "array",
					sheetRows: rowLimit + 1,
				};

				const workbook = XLSX.read(data, readOptions);

				if (!workbook || workbook.SheetNames.length === 0)
					return reject(
						"Invalid Excel file format or empty workbook"
					);

				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];

				const numRows: number = workbook["Strings"]?.length || 0;

				if (!worksheet) return reject("Empty worksheet");

				const rawJsonData: Array<Record<string, unknown>> =
					XLSX.utils.sheet_to_json(worksheet, {
						blankrows: true,
						defval: "",
					});

				if (!Array.isArray(rawJsonData) || rawJsonData.length === 0)
					return reject("No data found in the Excel file");

				if (numRows >= 11 && rawJsonData.length < 10) {
					const rowsToAppend = 10 - rawJsonData.length;
					const headers = Object.keys(rawJsonData[0]).filter(
						(k) => k !== "__rowNum__"
					);
					const emptyRow: Record<string, unknown> = {};
					for (const h of headers) {
						emptyRow[h] = "";
					}

					for (let i = 0; i < rowsToAppend; i++) {
						rawJsonData.Append({ ...emptyRow });
					}
				}

				const jsonData = rawJsonData.Map((o: any) => {
					if (util.IsPlainObject(o)) {
						return Object.entries(o).Fold(
							{} as Record<string, string>,
							(acc, [k, v]) => {
								// Drop xlsx's __rowNum__ metadata key — otherwise both the
								// column-count check and the empty-row check below would be
								// wrong (it counts as an extra column and always stringifies
								// to a non-empty value).
								if (k === "__rowNum__") return acc;
								if (k.ToLower().Contains("empty")) return acc;
								acc[k] =
									v !== null && v !== undefined
										? String(v).TrimSpace()
										: "";
								return acc;
							}
						);
					}
					throw new Error("Invalid row format");
				});

				if (!util.IsJsonValid(jsonData[0])) {
					return reject(
						"Invalid file format. Please upload a valid Excel file."
					);
				}

				const colCount = Object.keys(jsonData[0]).length;
				const asrsLayoutError = ValidateAsrsUploadColumnCount(
					colCount,
					options?.asrsUpload === true,
					options?.byLpn ?? false
				);
				if (asrsLayoutError) {
					return reject(asrsLayoutError);
				}

				const gateLayoutError = ValidateFlexNetUploadColumnCount(
					colCount,
					options?.partNumberRequired
				);
				if (gateLayoutError) {
					return reject(gateLayoutError);
				}

				if (colCount < 1 || colCount > maxAllowedCols) {
					return reject(
						`Invalid file format. File must have 1 or 2 columns, but found ${colCount}.`
					);
				}

				if (
					jsonData.All((row) =>
						Object.values(row).All(
							(value) => isNil(value) || value === ""
						)
					)
				) {
					return reject(
						`Invalid file format. ${rowLimit} rows after the header contain empty rows.`
					);
				}

				const ref = worksheet["!ref"];
				if (ref) {
					const [start, end] = ref.Split(":");
					if (!start || !end)
						return reject("Invalid sheet reference");

					const startCol = start.Replace(/[0-9]/g, "");
					const endCol = end.Replace(/[0-9]/g, "");

					if (options?.asrsUpload) {
						if (startCol !== "A" || endCol !== "A") {
							return reject(
								AsrsUploadSingleColumnRequired(
									options?.byLpn ?? false
								)
							);
						}
					} else if (
						startCol !== "A" ||
						(endCol !== "A" && endCol !== "B")
					) {
						return reject(
							"Invalid file format. File must only use columns A and B (no more than 2 columns)."
						);
					}
				}

				resolve(jsonData as Record<string, string>[]);
			} catch (error) {
				reject(
					error instanceof Error
						? error.message
						: "Error processing file"
				);
			}
		};

		reader.onerror = (e) => {
			reject(
				"Error reading file: " +
					(e.target?.error?.message || "Unknown error")
			);
		};

		reader.readAsArrayBuffer(file);
	});
}
